import DtoolsCustomerProposalTabbedPreview from "@/components/sales/dtools/DtoolsCustomerProposalTabbedPreview";
import DtoolsReviewLineCard from "@/components/sales/dtools/DtoolsReviewLineCard";
import DtoolsReviewSystemSection from "@/components/sales/dtools/DtoolsReviewSystemSection";
import DtoolsProposalPresentationHeader from "@/components/sales/dtools/DtoolsProposalPresentationHeader";
import { DtoolsProposalDisplayContext } from "@/components/sales/dtools/DtoolsProposalDisplayContext";
import ProposalApprovalPreview from "@/components/proposal/ProposalApprovalPreview";
import ProposalPdfDownloadButton from "@/components/proposal/ProposalPdfDownloadButton";
import { proposalPdfDownloadLabel, shouldSkipProposalCustomerEngagementTracking } from "@shared/proposalCustomerEngagement";
import ProposalPremiumShell from "@/components/proposal/ProposalPremiumShell";
import type { DtoolsOptionSectionActionsProps } from "@/components/sales/dtools/DtoolsOptionSectionActions";
import { PROPOSAL_COPY_FONT } from "@/lib/proposalTheme";
import type { ReactNode } from "react";
import {
  type DtoolsDisplaySettings,
  type DtoolsPortalPaymentTerm,
} from "@shared/dtoolsDisplaySettings";
import type { DtoolsJsonParseResult } from "@shared/dtoolsSiJsonImport";
import type { DtoolsReviewFooter, DtoolsReviewSystem } from "@shared/dtoolsStagingTransform";
import type { DtoolsSystemClassificationsMap, DtoolsTabModelVersion } from "@shared/dtoolsSystemClassification";
import { enrichDtoolsReviewBuildContext, type DtoolsReviewBuildContext } from "@shared/dtoolsReviewNavigation";
import {
  resolveProposalTitle,
  type DtoolsProposalPresentation,
  type ProposalPresentationHeaderInput,
} from "@shared/dtoolsProposalPresentation";
import type { ProposalCustomerPricingDisplay } from "@shared/proposalCustomerPricing";
import type { DtoolsPdfPackageMeta } from "@shared/dtoolsPdfPackage";
import {
  DTOOLS_CUSTOMER_PREVIEW_VISIBILITY_AUDIENCE,
  filterVisibleDtoolsSystems,
} from "@shared/dtoolsOptionVisibility";

export type DtoolsProposalCoverInput = {
  customerName: string;
  proposalTitle: string;
  proposalRef: string;
};

type Props = {
  systems: DtoolsReviewSystem[];
  footer: DtoolsReviewFooter;
  parseResult?: DtoolsJsonParseResult | null;
  paymentTerms?: DtoolsPortalPaymentTerm[];
  displaySettings?: DtoolsDisplaySettings;
  reviewContext?: DtoolsReviewBuildContext;
  presentation?: DtoolsProposalPresentation;
  headerPresentation?: ProposalPresentationHeaderInput;
  /** Sandbox review shows a placeholder; live Link A/B omit it. */
  showApprovalPlaceholder?: boolean;
  /** Retail-style cover block (customer name, title, ref). */
  cover?: DtoolsProposalCoverInput | null;
  /** Customer vs staff summary behaviour. */
  audience?: "customer" | "staff";
  /** Staff sandbox: message when Cleo description/hover is missing. */
  cleoDescriptionPendingMessage?: string;
  optionActions?: Omit<DtoolsOptionSectionActionsProps, "optionLabel">;
  optionSectionIds?: string[];
  navigationSystemKey?: string | null;
  navigationRequestToken?: number;
  useTabbedCustomerLayout?: boolean;
  /** Renders below the cover, above tabs/content (e.g. revision notice). */
  contentLead?: ReactNode;
  tabModelVersion?: DtoolsTabModelVersion;
  systemClassifications?: DtoolsSystemClassificationsMap;
  parsedMetadataJson?: unknown;
  customerPricing?: ProposalCustomerPricingDisplay | null;
  retailPricingJson?: {
    listTotal?: number | null;
    originalListPrice?: number | null;
    agreedPriceSgd?: number | null;
    agreedPrice?: number | null;
  } | null;
  totalAmount?: number | string | null;
  pdfPackageMeta?: DtoolsPdfPackageMeta;
  linkBExpiresAt?: string | Date | null;
  /** Step 3 / Revise only: exclusive Web vs PDF accordion. */
  dualPreviewAccordion?: boolean;
};

export default function DtoolsReviewProposalPreview({
  systems,
  footer,
  parseResult,
  paymentTerms = [],
  displaySettings = {},
  reviewContext,
  presentation,
  headerPresentation,
  showApprovalPlaceholder = true,
  cover,
  audience = "customer",
  cleoDescriptionPendingMessage,
  optionActions,
  optionSectionIds,
  navigationSystemKey,
  navigationRequestToken,
  useTabbedCustomerLayout = true,
  contentLead,
  tabModelVersion,
  systemClassifications,
  parsedMetadataJson,
  customerPricing,
  retailPricingJson,
  totalAmount,
  pdfPackageMeta,
  linkBExpiresAt,
  dualPreviewAccordion = false,
}: Props) {
  const enrichedContext = enrichDtoolsReviewBuildContext(systems, {
    ...reviewContext,
    presentation: reviewContext?.presentation ?? presentation,
    systemClassifications: systemClassifications ?? reviewContext?.systemClassifications,
  });
  const previewVisibilityAudience = DTOOLS_CUSTOMER_PREVIEW_VISIBILITY_AUDIENCE;
  const visibleSystems = filterVisibleDtoolsSystems(systems, displaySettings, {
    audience: previewVisibilityAudience,
  });
  const createdAt = parseResult?.project.createdOn ?? parseResult?.project.updatedOn;

  const resolvedCover =
    cover ??
    (headerPresentation
      ? {
          customerName:
            String(headerPresentation.customerNameSnapshot ?? parseResult?.customer?.clientName ?? "").trim() ||
            "Valued Customer",
          proposalTitle: resolveProposalTitle(headerPresentation, presentation),
          proposalRef: String(headerPresentation.proposalRef ?? headerPresentation.projectNumber ?? "—").trim() || "—",
        }
      : null);

  const fullViewportCover = Boolean(resolvedCover);

  const shellContent = (
    <>
      {presentation && headerPresentation && !resolvedCover ? (
        <DtoolsProposalPresentationHeader
          header={headerPresentation}
          presentation={presentation}
          contentOnly={false}
        />
      ) : null}

      {useTabbedCustomerLayout ? (
        <DtoolsCustomerProposalTabbedPreview
          systems={systems}
          footer={footer}
          reviewContext={enrichedContext}
          presentation={presentation}
          paymentTerms={paymentTerms}
          createdAt={createdAt}
          linkBExpiresAt={linkBExpiresAt ?? headerPresentation?.linkBExpiresAt ?? null}
          audience={audience}
          optionActions={optionActions}
          displaySettings={displaySettings}
          tabModelVersion={tabModelVersion}
          systemClassifications={systemClassifications}
          parsedMetadataJson={parsedMetadataJson}
          customerPricing={customerPricing}
          retailPricingJson={retailPricingJson}
          totalAmount={totalAmount}
          cover={resolvedCover}
          pdfPackageMeta={pdfPackageMeta}
          dualPreviewAccordion={dualPreviewAccordion}
          optionSectionIds={optionSectionIds}
          navigationSystemKey={navigationSystemKey}
          navigationRequestToken={navigationRequestToken}
        />
      ) : (
        <>
          {visibleSystems.map(({ system: sys, optionIndex }) => (
            <DtoolsReviewSystemSection
              key={sys.systemId ?? sys.name}
              system={sys}
              reviewContext={enrichedContext}
              optionIndex={optionIndex}
              optionActions={optionActions}
              sectionId={optionSectionIds?.[optionIndex]}
            />
          ))}
          {footer.adjustments.map((line) => (
            <DtoolsReviewLineCard key={line.id} line={line} compact />
          ))}
        </>
      )}

      {showApprovalPlaceholder ? <ProposalApprovalPreview /> : null}

      {optionActions?.proposalId ? (
        <div
          className="proposal-no-print flex justify-center border-t border-[#1A2744]/10 pt-10"
          data-testid="proposal-customer-pdf-footer"
        >
          <ProposalPdfDownloadButton
            proposalId={optionActions.proposalId}
            proposalRef={optionActions.proposalRef}
            customerEmail={optionActions.customerEmail}
            linkBCredential={optionActions.linkBCredential}
            label={proposalPdfDownloadLabel({
              audience,
              approvalDisabled: optionActions.approvalDisabled,
              isSandbox: optionActions.isSandbox,
            })}
            className="min-h-11 border-[#1A2744]/20 text-[#1A2744]"
            variant="outline"
            skipAnalytics={shouldSkipProposalCustomerEngagementTracking({
              audience,
              approvalDisabled: optionActions.approvalDisabled,
              isSandbox: optionActions.isSandbox,
            })}
            parsedMetadataJson={parsedMetadataJson}
          />
        </div>
      ) : optionActions?.isSandbox ? (
        <div className="proposal-no-print flex justify-center border-t border-[#1A2744]/10 pt-10">
          <ProposalPdfDownloadButton
            proposalId={0}
            label="Print preview"
            className="min-h-11 border-[#1A2744]/20 text-[#1A2744]"
            variant="outline"
            skipAnalytics
            printOnly
          />
        </div>
      ) : null}
    </>
  );

  return (
    <DtoolsProposalDisplayContext.Provider
      value={{
        ...displaySettings,
        audience,
        cleoDescriptionPendingMessage,
        fullViewportCover,
        showInternalMarkupBasePrices:
          audience === "staff" &&
          Boolean((retailPricingJson as Record<string, unknown> | null | undefined)?.markupEnabled),
      }}
    >
      {resolvedCover ? (
        <ProposalPremiumShell cover={resolvedCover} fullViewportCover={fullViewportCover} contentLead={contentLead ? <div className="dtools-proposal-screen-only">{contentLead}</div> : undefined}>
          {shellContent}
        </ProposalPremiumShell>
      ) : (
        <div className="w-full space-y-10" style={{ fontFamily: PROPOSAL_COPY_FONT }}>
          {contentLead}
          {shellContent}
        </div>
      )}
    </DtoolsProposalDisplayContext.Provider>
  );
}
